import type { UseFormReturn } from 'react-hook-form';
import type { z } from 'zod';

import type { ConsentDataType } from '@/services/consents/types';

import type { MappedConsentType } from '@/components/organisms/RegistrationForm/RegistrationForm.types';
import type { RegistrationFormSchema } from '@/components/organisms/RegistrationForm/utils/RegistrationFormSchema';

export const handleConsentsState = (
    formProviderConfig: UseFormReturn<z.infer<typeof RegistrationFormSchema>>,
    registrationConsents: MappedConsentType[],
    fieldName: keyof z.infer<typeof RegistrationFormSchema>,
    value?: boolean,
) => {
    const { trigger, setValue, getValues } = formProviderConfig;

    const validationOptions = {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true,
    };

    setValue(fieldName, !value);

    trigger(fieldName);

    if (fieldName === 'agreementsAcceptance') {
        registrationConsents.forEach(({ slug }) => {
            const key = slug as keyof z.infer<typeof RegistrationFormSchema>;

            setValue(key, !value, validationOptions);

            trigger(key);
        });
    } else {
        const isEveryConsentChecked = registrationConsents.every(
            ({ slug }) =>
                !!getValues(
                    slug as keyof z.infer<typeof RegistrationFormSchema>,
                ),
        );

        setValue(
            'agreementsAcceptance',
            isEveryConsentChecked,
            validationOptions,
        );
    }
};

export const formatConsentData = ({
    slug,
    currentVersion,
    required,
}: ConsentDataType): MappedConsentType => {
    let fieldName;

    switch (slug) {
        case 'marketing':
            fieldName = 'marketingAcceptance';
            break;
        case 'rules':
            fieldName = 'regulationsAcceptance';
            break;
        default:
            fieldName = `${slug.replace(/_([a-z])/g, (_, letter) =>
                letter.toUpperCase(),
            )}Acceptance`;
    }

    return {
        slug,
        required,
        fieldName, //name for ga statistics
        consentVersionId: currentVersion.id,
        content: currentVersion.content,
    };
};
